import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../Context/AuthContext";
import { getAdminRoleAccess } from "../../access/adminAccess";
import logo from "../../Assets/logo.png";

const getActivePageLabel = (pathname, userRole) => {
  const menuItems = getAdminRoleAccess(userRole)?.menu || [];
  const activeItem = menuItems.find((item) => {
    if (item.path === "/auth/pos-pages") return pathname === item.path;
    return pathname === item.path || pathname.startsWith(`${item.path}/`);
  });

  return activeItem?.label || "لوحة الإدارة";
};

export default function AdminHeader({
  sidebarWidth = 0,
  anchor = "left",
  isMobile: isMobileProp,
  onOpenSidebar,
}) {
  const { user, logout, userRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobile = Boolean(isMobileProp);
  const activePageLabel = getActivePageLabel(location.pathname, userRole);

  const handleLogout = () => {
    logout();
    navigate("/background/gate/login");
  };

  const offsetStyle = anchor === "left"
    ? { ml: { xs: 0, md: `${sidebarWidth}px` } }
    : { mr: { xs: 0, md: `${sidebarWidth}px` } };

  return (
    <AppBar
      dir="rtl"
      position="fixed"
      elevation={0}
      sx={{
        width: { xs: "100%", md: `calc(100% - ${sidebarWidth}px)` },
        ...offsetStyle,
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
        color: "text.primary",
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.06)}`,
        backdropFilter: "blur(8px)",
        zIndex: (t) => t.zIndex.drawer + 1,
        transition: (t) =>
          t.transitions.create(["margin", "width"], {
            easing: t.transitions.easing.sharp,
            duration: t.transitions.duration.shorter,
          }),
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: { xs: 1, sm: 1.5 },
          px: { xs: 1, sm: 2, md: 2.5 },
          minHeight: { xs: 58, sm: 64 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.75, sm: 1.25 }, minWidth: 0 }}>
          {isMobile && (
            <IconButton
              onClick={onOpenSidebar}
              aria-label="open admin sidebar"
              sx={{
                border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                borderRadius: 2,
                color: "primary.main",
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                flexShrink: 0,
              }}
            >
              <MenuRoundedIcon />
            </IconButton>
          )}

          <Box
            sx={{
              width: { xs: 34, sm: 40 },
              height: { xs: 34, sm: 40 },
              borderRadius: 2,
              overflow: "hidden",
              display: "grid",
              placeItems: "center",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Nuvvy"
              sx={{ width: "85%", height: "85%", objectFit: "contain" }}
            />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant={isSmallScreen ? "subtitle2" : "subtitle1"}
              sx={{ fontWeight: 900, lineHeight: 1.1 }}
              noWrap
            >
              Nuvvy
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", fontWeight: 800, lineHeight: 1.15 }}
              noWrap
            >
              {activePageLabel}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.75, sm: 1.25 }, flexShrink: 0 }}>
          <Tooltip title="شاشة البيع" arrow>
            <Button
              variant="contained"
              size={isSmallScreen ? "small" : "medium"}
              startIcon={!isSmallScreen ? <PointOfSaleIcon /> : null}
              onClick={() => navigate("/auth/pos-pages")}
              sx={{
                minWidth: isSmallScreen ? 38 : "auto",
                width: isSmallScreen ? 38 : "auto",
                height: isSmallScreen ? 38 : 40,
                borderRadius: 2,
                fontWeight: 800,
                px: { xs: 0, sm: 1.75 },
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.dark, 0.9)})`,
                boxShadow: `0 3px 10px ${alpha(theme.palette.primary.main, 0.28)}`,
              }}
            >
              {isSmallScreen ? <PointOfSaleIcon fontSize="small" /> : "شاشة البيع"}
            </Button>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" }, opacity: 0.55 }} />

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, minWidth: 0 }}>
            {!isSmallScreen && (
              <Box sx={{ textAlign: "right", minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.2 }} noWrap>
                  {user?.name || "Admin"}
                </Typography>
                <Chip
                  label={userRole || "admin"}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 18, fontSize: "0.68rem", fontWeight: 800, mt: 0.3 }}
                />
              </Box>
            )}

            <Avatar
              sx={{
                width: { xs: 34, sm: 38 },
                height: { xs: 34, sm: 38 },
                bgcolor: "primary.main",
                fontWeight: 900,
                fontSize: "0.95rem",
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.35)}`,
                border: `2px solid ${alpha(theme.palette.primary.main, 0.22)}`,
              }}
            >
              {user?.name?.charAt(0) || "A"}
            </Avatar>
          </Box>

          <Tooltip title="تسجيل الخروج" arrow>
            <IconButton
              onClick={handleLogout}
              aria-label="logout"
              size={isSmallScreen ? "small" : "medium"}
              sx={{
                width: { xs: 38, sm: 40 },
                height: { xs: 38, sm: 40 },
                color: "error.main",
                border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.error.main, 0.03),
                "&:hover": {
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                  borderColor: "error.main",
                },
              }}
            >
              <ExitToAppIcon fontSize={isSmallScreen ? "small" : "medium"} />
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
